import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { API, api, fmt } from "@/lib/api";
import { MENU } from "@/data/menu";
import SandwichBuilder from "./SandwichBuilder";
import Reveal from "./Reveal";

const CHALK_COFFEE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_7fb684a5-db32-4073-aa6c-6bc4081bd17c/artifacts/b2vi89y2_IMG_3135.webp";
const CHALK_DELI =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_7fb684a5-db32-4073-aa6c-6bc4081bd17c/artifacts/z3f8sl06_IMG_3136.webp";

const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function MenuSection() {
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [liveItems, setLiveItems] = useState(null);
  const [liveCats, setLiveCats] = useState([]);

  useEffect(() => {
    api
      .get("/menu")
      .then((r) => setLiveItems(r.data))
      .catch(() => setLiveItems(null));
    api
      .get("/categories")
      .then((r) => setLiveCats(r.data))
      .catch(() => {});
  }, []);

  const source = useMemo(() => {
    if (!liveItems || liveItems.length === 0) return MENU;
    const byCat = {};
    for (const it of liveItems) {
      (byCat[it.category] ||= []).push({
        title: it.title,
        desc: it.desc,
        price: fmt(it.price_cents),
        photo: it.photo_path ? `${API}/files/${it.photo_path}` : null,
        sub: it.subcategory || "",
      });
    }
    const out = [];
    for (const c of MENU) {
      if (c.builder) {
        out.push(c);
        continue;
      }
      const items = byCat[c.id] || [];
      if (items.length > 0) out.push({ ...c, items });
    }
    for (const lc of liveCats) {
      if (MENU.some((c) => c.id === lc.id)) continue;
      const items = byCat[lc.id] || [];
      if (items.length > 0) {
        out.push({
          id: lc.id,
          label: lc.label,
          short: lc.label,
          time: null,
          items,
          subs: lc.subs || [],
        });
      }
    }
    return out;
  }, [liveItems, liveCats]);

  const tabs = useMemo(
    () => [{ id: "all", label: "Everything" }, ...source.map((c) => ({ id: c.id, label: c.short }))],
    [source]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return source
      .filter((c) => tab === "all" || c.id === tab)
      .map((c) => ({
        ...c,
        builder: c.builder && (!q || "build your own sandwich".includes(q)),
        items: c.items.filter(
          (it) => !q || `${it.title} ${it.desc}`.toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.builder || c.items.length > 0);
  }, [tab, query, source]);

  const groupBySub = (cat) => {
    const groups = [];
    const seen = new Map();
    for (const it of cat.items) {
      const key = it.sub || "";
      if (!seen.has(key)) {
        seen.set(key, []);
        groups.push([key, seen.get(key)]);
      }
      seen.get(key).push(it);
    }
    return groups;
  };

  const subLabel = (cat, key) =>
    cat.subs?.find((s) => s.id === key)?.label || key;

  return (
    <section id="menu" data-testid="menu-section" className="bg-sage py-24 lg:py-32">
      <div className="mx-auto max-w-6xl px-5">
        <Reveal>
          <p className="text-center font-editorial text-sm font-semibold italic tracking-[0.3em] text-grape">
            02 — THE CHALKBOARD, TRANSCRIBED
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mx-auto mt-4 max-w-2xl text-center font-display text-4xl font-bold leading-[1.05] text-chalk sm:text-5xl lg:text-6xl">
            The whole menu, <span className="text-artichoke">hot off the board.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
            <img
              src={CHALK_COFFEE}
              alt="Coffee and breakfast chalkboard menu"
              className="h-40 w-auto -rotate-2 rounded-xl border-4 border-cream object-cover shadow-xl transition-transform hover:rotate-0 sm:h-48"
              data-testid="chalkboard-photo-1"
            />
            <img
              src={CHALK_DELI}
              alt="Sandwich and lunch chalkboard menu"
              className="h-40 w-auto rotate-2 rounded-xl border-4 border-cream object-cover shadow-xl transition-transform hover:rotate-0 sm:h-48"
              data-testid="chalkboard-photo-2"
            />
          </div>
          <p className="mt-3 text-center font-editorial text-xs italic text-chalk/45">
            straight off the chalkboard
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-col items-center gap-5">
            <div className="flex max-w-full gap-2 overflow-x-auto pb-1" data-testid="menu-tabs">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  data-testid={`menu-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-bold transition-all active:scale-95 ${
                    tab === t.id
                      ? "bg-grape text-cream shadow-lg shadow-grape/25"
                      : "bg-white text-chalk/60 hover:text-grape"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-chalk/40" />
              <input
                data-testid="menu-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Craving something?"
                className="w-full rounded-full border-2 border-chalk/10 bg-white py-2.5 pl-11 pr-4 text-sm font-medium text-chalk outline-none transition-colors placeholder:text-chalk/35 focus:border-artichoke"
              />
            </div>
          </div>
        </Reveal>

        {visible.map((cat) => (
          <div key={cat.id} className="mt-16">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-chalk/10 pb-3">
                <h3
                  className="font-display text-2xl font-bold text-artichoke sm:text-3xl"
                  data-testid={`menu-category-${cat.id}`}
                >
                  {cat.label}
                </h3>
                {cat.time && (
                  <span className="rounded-full bg-gold/20 px-4 py-1.5 font-editorial text-sm font-semibold italic text-crispy">
                    {cat.time}
                  </span>
                )}
              </div>
            </Reveal>
            {cat.builder ? (
              <SandwichBuilder />
            ) : (
              groupBySub(cat).map(([subKey, gItems]) => (
                <div key={subKey || "main"} className="mt-7">
                  {subKey && (
                    <p
                      className="mb-4 font-editorial text-lg font-semibold italic text-grape"
                      data-testid={`menu-sub-${cat.id}-${subKey}`}
                    >
                      {subLabel(cat, subKey)}
                    </p>
                  )}
                  <div className="grid gap-x-10 gap-y-6 md:grid-cols-2">
                    {gItems.map((it, i) => (
                      <Reveal key={it.title} delay={Math.min(i * 0.05, 0.3)}>
                        <div
                          data-testid={`menu-item-${slug(it.title)}`}
                          className="group flex items-start gap-3"
                        >
                          {it.photo && (
                            <img
                              src={it.photo}
                              alt={it.title}
                              className="h-14 w-14 shrink-0 rounded-xl border border-leafline object-cover"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                              <h4 className="font-semibold text-chalk transition-colors group-hover:text-grape">
                                {it.title}
                              </h4>
                              <span className="mx-1 flex-1 border-b-2 border-dotted border-artichoke/30" />
                              <span className="whitespace-nowrap font-editorial text-lg font-semibold text-crispy">
                                {it.price}
                              </span>
                            </div>
                            {it.desc && (
                              <p className="mt-1.5 pr-10 text-sm leading-relaxed text-chalk/55">
                                {it.desc}
                              </p>
                            )}
                          </div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        ))}

        {visible.length === 0 && (
          <p
            data-testid="menu-empty"
            className="mt-14 text-center font-editorial text-lg italic text-chalk/50"
          >
            Nothing on the board matches — try another craving.
          </p>
        )}
      </div>
    </section>
  );
}
